/**
 * edj-predictive-search.js
 * Custom element <edj-predictive-search> — overlay header prédictif EDJ.
 * Lot 4 Batch D — EDJ Shopify theme
 *
 * Source de vérité : design-system/edj/project/page-search.jsx (SearchOverlay).
 *
 * Branche le bouton [data-action="open-search"] global → ouvre le <dialog>.
 * Fetch /search/suggest.json (debounce 200ms) → render résultats côté client.
 *
 * Sécurité XSS : on n'utilise JAMAIS innerHTML avec du contenu provenant
 * de la réponse API. Les titres/textes passent par textContent ou par
 * le helper highlightInto() qui crée les nœuds DOM via createTextNode +
 * <mark> (pas d'injection HTML brute).
 */

(function () {
  'use strict';

  const SUGGEST_URL = '/search/suggest.json';
  const DEBOUNCE_MS = 200;
  const MIN_CHARS = 2;
  const MAX_PRODUCTS = 5;
  const MAX_ARTICLES = 3;
  const MAX_PAGES = 3;

  /**
   * Highlight des termes du query dans un texte.
   * Insère le résultat (text + <mark>) dans `target` via DOM safe APIs.
   */
  function highlightInto(target, text, q) {
    if (!target) return;
    target.textContent = '';
    if (!text) return;
    if (!q) {
      target.appendChild(document.createTextNode(text));
      return;
    }
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) {
      target.appendChild(document.createTextNode(text));
      return;
    }
    target.appendChild(document.createTextNode(text.slice(0, i)));
    const mark = document.createElement('mark');
    mark.className = 'edj-predictive__mark';
    mark.appendChild(document.createTextNode(text.slice(i, i + q.length)));
    target.appendChild(mark);
    target.appendChild(document.createTextNode(text.slice(i + q.length)));
  }

  function debounce(fn, delay) {
    let t = null;
    return function (...args) {
      if (t) clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  class EdjPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this._lastTrigger = null;
      this._fetchAbort = null;
      this._onInputDebounced = debounce(this._onInput.bind(this), DEBOUNCE_MS);
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-predictive-dialog]');
      this.input = this.querySelector('[data-predictive-input]');
      this.clearBtn = this.querySelector('[data-predictive-clear]');
      this.form = this.querySelector('[data-predictive-form]');
      this.body = this.querySelector('[data-predictive-body]');
      this.initialEl = this.querySelector('[data-predictive-initial]');
      this.resultsEl = this.querySelector('[data-predictive-results]');
      this.emptyEl = this.querySelector('[data-predictive-empty]');
      this.loadingEl = this.querySelector('[data-predictive-loading]');

      // Triggers globaux : tout [data-action="open-search"]
      document.addEventListener('click', this._onTriggerClick.bind(this));

      // Fermeture
      this.querySelectorAll('[data-close-overlay]').forEach((el) => {
        el.addEventListener('click', this.close.bind(this));
      });

      // Escape natif <dialog>
      if (this.dialog) {
        this.dialog.addEventListener('cancel', (e) => {
          e.preventDefault();
          this.close();
        });
      }

      // Input + clear
      if (this.input) {
        this.input.addEventListener('input', this._onInputDebounced);
        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.close();
        });
      }
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          if (!this.input) return;
          this.input.value = '';
          this.clearBtn.hidden = true;
          this._showInitial();
          this.input.focus();
        });
      }
    }

    _onTriggerClick(e) {
      const trigger = e.target.closest('[data-action="open-search"]');
      if (!trigger) return;
      e.preventDefault();
      this._lastTrigger = trigger;
      this.open();
    }

    open() {
      if (!this.dialog) return;
      if (typeof this.dialog.showModal === 'function') {
        try { this.dialog.showModal(); } catch (_) { this.dialog.setAttribute('open', ''); }
      } else {
        this.dialog.setAttribute('open', '');
      }
      document.body.style.overflow = 'hidden';
      if (this.input) {
        this.input.value = '';
        setTimeout(() => this.input.focus(), 30);
      }
      if (this.clearBtn) this.clearBtn.hidden = true;
      this._showInitial();
    }

    close() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function') {
        try { this.dialog.close(); } catch (_) { this.dialog.removeAttribute('open'); }
      } else {
        this.dialog.removeAttribute('open');
      }
      document.body.style.overflow = '';
      if (this._lastTrigger && typeof this._lastTrigger.focus === 'function') {
        this._lastTrigger.focus();
      }
    }

    _onInput() {
      const q = (this.input && this.input.value || '').trim();
      if (this.clearBtn) this.clearBtn.hidden = q.length === 0;

      if (q.length < MIN_CHARS) {
        this._showInitial();
        return;
      }
      this._fetch(q);
    }

    _showInitial() {
      this.initialEl && (this.initialEl.hidden = false);
      this.resultsEl && (this.resultsEl.hidden = true);
      this.emptyEl && (this.emptyEl.hidden = true);
      this.loadingEl && (this.loadingEl.hidden = true);
    }

    _showLoading() {
      this.initialEl && (this.initialEl.hidden = true);
      this.resultsEl && (this.resultsEl.hidden = true);
      this.emptyEl && (this.emptyEl.hidden = true);
      this.loadingEl && (this.loadingEl.hidden = false);
    }

    _showResults() {
      this.initialEl && (this.initialEl.hidden = true);
      this.resultsEl && (this.resultsEl.hidden = false);
      this.emptyEl && (this.emptyEl.hidden = true);
      this.loadingEl && (this.loadingEl.hidden = true);
    }

    _showEmpty() {
      this.initialEl && (this.initialEl.hidden = true);
      this.resultsEl && (this.resultsEl.hidden = true);
      this.emptyEl && (this.emptyEl.hidden = false);
      this.loadingEl && (this.loadingEl.hidden = true);
    }

    async _fetch(q) {
      if (this._fetchAbort) {
        try { this._fetchAbort.abort(); } catch (_) {}
      }
      const controller = new AbortController();
      this._fetchAbort = controller;
      this._showLoading();

      const params = new URLSearchParams();
      params.set('q', q);
      params.append('resources[type]', 'product,article,page');
      params.append('resources[limit]', String(Math.max(MAX_PRODUCTS, MAX_ARTICLES, MAX_PAGES)));
      params.append('resources[options][unavailable_products]', 'last');
      params.append('resources[options][fields]', 'title,product_type,vendor,body');

      try {
        const res = await fetch(`${SUGGEST_URL}?${params.toString()}`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        const resources = (data && data.resources && data.resources.results) || {};
        this._render(q, resources);
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        console.warn('[edj-predictive-search] fetch error', err);
        this._showInitial();
      }
    }

    _render(q, resources) {
      const products = (resources.products || []).slice(0, MAX_PRODUCTS);
      const articles = (resources.articles || []).slice(0, MAX_ARTICLES);
      const pages = (resources.pages || []).slice(0, MAX_PAGES);

      const total = products.length + articles.length + pages.length;
      if (total === 0) {
        this._renderEmpty(q);
        this._showEmpty();
        return;
      }

      const tplResults = this.querySelector('[data-predictive-template="results"]');
      if (!tplResults) return;
      const frag = tplResults.content.cloneNode(true);
      const root = frag.firstElementChild;

      // "Voir tous les résultats →"
      const seeAll = root.querySelector('[data-slot="see-all"]');
      if (seeAll) {
        seeAll.setAttribute('href', '/search?q=' + encodeURIComponent(q));
        seeAll.textContent = 'Voir tous les résultats →';
      }
      const productsCount = root.querySelector('[data-slot="products-count"]');
      if (productsCount) {
        productsCount.textContent = 'Produits (' + products.length + ')';
      }

      // Suggestions inline (q + qualificateurs)
      const suggestions = [
        q + ' en stock',
        q + ' pas cher',
        q + ' cadeau',
      ];
      const suggBox = root.querySelector('[data-slot="suggestions"]');
      if (suggBox) {
        // textContent sécurise + ajout des labels par DOM
        suggBox.textContent = '';
        const lbl = document.createElement('div');
        lbl.className = 'edj-predictive__sublabel';
        lbl.textContent = 'Suggestions';
        suggBox.appendChild(lbl);
        suggestions.forEach((s) => {
          const tpl = this.querySelector('[data-predictive-template="suggestion-row"]');
          if (!tpl) return;
          const item = tpl.content.cloneNode(true);
          const a = item.querySelector('[data-slot="link"]');
          const text = item.querySelector('[data-slot="text"]');
          if (a) a.setAttribute('href', '/search?q=' + encodeURIComponent(s));
          if (text) highlightInto(text, s, q);
          suggBox.appendChild(item);
        });
      }

      // Produits
      const productsBox = root.querySelector('[data-slot="products"]');
      if (productsBox) {
        productsBox.textContent = '';
        products.forEach((p) => {
          const tpl = this.querySelector('[data-predictive-template="product-row"]');
          if (!tpl) return;
          const item = tpl.content.cloneNode(true);
          const a = item.querySelector('[data-slot="link"]');
          const img = item.querySelector('[data-slot="img"]');
          const title = item.querySelector('[data-slot="title"]');
          const meta = item.querySelector('[data-slot="meta"]');
          const price = item.querySelector('[data-slot="price"]');
          const compare = item.querySelector('[data-slot="compare"]');

          if (a) a.setAttribute('href', p.url);
          if (img && p.image) {
            img.style.backgroundImage = 'url(' + encodeURI(p.image) + ')';
          } else if (img) {
            img.classList.add('edj-predictive__product-img--fallback');
          }
          if (title) highlightInto(title, p.title || '', q);
          if (meta) {
            const parts = [];
            if (p.product_type) parts.push(p.product_type);
            if (p.vendor) parts.push(p.vendor);
            meta.textContent = parts.join(' · ');
          }
          if (price) price.textContent = formatMoney(p.price);
          if (compare && p.compare_at_price && Number(p.compare_at_price) > Number(p.price)) {
            compare.textContent = formatMoney(p.compare_at_price);
            compare.hidden = false;
          }
          productsBox.appendChild(item);
        });
      }

      // Articles
      const articlesBox = root.querySelector('[data-slot="articles"]');
      if (articlesBox) {
        articlesBox.textContent = '';
        if (articles.length > 0) {
          const lbl = document.createElement('div');
          lbl.className = 'edj-predictive__sublabel edj-predictive__sublabel--inline';
          lbl.textContent = 'Le Journal (' + articles.length + ')';
          articlesBox.appendChild(lbl);
        }
        articles.forEach((a) => {
          const tpl = this.querySelector('[data-predictive-template="article-row"]');
          if (!tpl) return;
          const item = tpl.content.cloneNode(true);
          const link = item.querySelector('[data-slot="link"]');
          const title = item.querySelector('[data-slot="title"]');
          const meta = item.querySelector('[data-slot="meta"]');
          if (link) link.setAttribute('href', a.url);
          if (title) highlightInto(title, a.title || '', q);
          if (meta && a.summary_html) {
            // Strip HTML via DOM (pas innerHTML : on utilise un parser sans exécution)
            const tmp = document.createElement('template');
            tmp.innerHTML = ''; // template content n'est pas exécuté
            // Approche safe : on parse le summary via DOMParser → textContent
            const doc = new DOMParser().parseFromString(a.summary_html, 'text/html');
            const text = (doc.body && doc.body.textContent || '').trim();
            meta.textContent = text.slice(0, 90) + (text.length > 90 ? '…' : '');
          }
          articlesBox.appendChild(item);
        });
      }

      // Pages
      const pagesBox = root.querySelector('[data-slot="pages"]');
      if (pagesBox) {
        pagesBox.textContent = '';
        if (pages.length > 0) {
          const lbl = document.createElement('div');
          lbl.className = 'edj-predictive__sublabel edj-predictive__sublabel--inline';
          lbl.textContent = 'Pages (' + pages.length + ')';
          pagesBox.appendChild(lbl);
        }
        pages.forEach((pg) => {
          const tpl = this.querySelector('[data-predictive-template="page-row"]');
          if (!tpl) return;
          const item = tpl.content.cloneNode(true);
          const link = item.querySelector('[data-slot="link"]');
          const title = item.querySelector('[data-slot="title"]');
          if (link) link.setAttribute('href', pg.url);
          if (title) highlightInto(title, pg.title || '', q);
          pagesBox.appendChild(item);
        });
      }

      // Inject : on remplace tout le contenu du conteneur par le fragment cloné (pas d'innerHTML utilisateur)
      this.resultsEl.textContent = '';
      this.resultsEl.appendChild(root);
      this._showResults();
    }

    _renderEmpty(q) {
      const tpl = this.querySelector('[data-predictive-template="empty"]');
      if (!tpl || !this.emptyEl) return;
      const frag = tpl.content.cloneNode(true);
      const queryEl = frag.querySelector('[data-slot="query"]');
      if (queryEl) queryEl.textContent = '« ' + q + ' »';
      this.emptyEl.textContent = '';
      this.emptyEl.appendChild(frag);
    }
  }

  /**
   * Format prix Shopify Predictive Search.
   * `price` retourné en string ("33.90") ou number.
   */
  function formatMoney(value) {
    if (value == null || value === '') return '';
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(num)) return '';
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR',
        maximumFractionDigits: num % 1 === 0 ? 0 : 2,
      }).format(num);
    } catch (_) {
      return num.toFixed(2).replace('.', ',') + ' €';
    }
  }

  if (!customElements.get('edj-predictive-search')) {
    customElements.define('edj-predictive-search', EdjPredictiveSearch);
  }
})();
